/**
 * The console's display-language preference (doc/20261023-i18n-zh-cn.md §3.2): the shared client logic, wired to this
 * origin's storage, the control plane's account save and the console's i18n. A control-plane account's preference is
 * its own, never synced with a zone's.
 */
import { useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DEFAULT_DISPLAY_LANGUAGE,
  isSupportedDisplayLanguage,
  SUPPORTED_DISPLAY_LANGUAGES,
  type DisplayLanguageId,
  type DisplayLanguageInfo,
} from '../../shared/locale/locale.mjs';
import { createDisplayLanguagePreference, STORAGE_KEY } from '../../shared/locale/preference.mjs';
import { controlApi } from './control/api';
import { applyDisplayLanguage } from './i18n';

export const displayLanguage = createDisplayLanguagePreference({
  storage: {
    read() {
      try {
        return window.localStorage.getItem(STORAGE_KEY);
      } catch {
        return null; // Private mode, or storage disabled: the choice lasts as long as the page.
      }
    },
    write(text) {
      try {
        window.localStorage.setItem(STORAGE_KEY, text);
      } catch {
        // Remembering is a nicety; showing is not.
      }
    },
    remove() {
      try {
        window.localStorage.removeItem(STORAGE_KEY);
      } catch {
        // As above.
      }
    },
  },
  save: (language, chosenAt) => controlApi.saveDisplayLanguage(language, chosenAt),
  show: (language) => applyDisplayLanguage(isSupportedDisplayLanguage(language) ? language : 'en'),
});

export function browserLanguages(): readonly string[] {
  return navigator.languages?.length ? navigator.languages : [navigator.language];
}

let offered: readonly DisplayLanguageInfo[] = SUPPORTED_DISPLAY_LANGUAGES;
const listeners = new Set<() => void>();

/** Only the languages both this build and the control plane accept are offered (§3.3). */
export function setServerLanguages(languages: readonly string[] | undefined): void {
  const next = languages === undefined ? SUPPORTED_DISPLAY_LANGUAGES
    : SUPPORTED_DISPLAY_LANGUAGES.filter((language) => languages.includes(language.id));
  offered = next.length === 0 ? SUPPORTED_DISPLAY_LANGUAGES.filter((language) => language.id === 'en') : next;
  for (const listener of listeners) listener();
}

export function useDisplayLanguage(): {
  readonly language: DisplayLanguageId;
  readonly languages: readonly DisplayLanguageInfo[];
  readonly choose: (language: DisplayLanguageId) => void;
} {
  const { i18n } = useTranslation();
  const languages = useSyncExternalStore((listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  }, () => offered);
  const language = isSupportedDisplayLanguage(i18n.language) ? i18n.language : DEFAULT_DISPLAY_LANGUAGE;
  return { language, languages, choose: (next) => void displayLanguage.choose(next) };
}
